"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.appendIsNewIssueToRawResults = appendIsNewIssueToRawResults;
const createFingerprints_1 = require("./createFingerprints");
/**
 *
 * @param currentResults - the current watcher results
 * @param previousResults - the previous watcher results if they exist
 * @returns the current watcher results with a new property `isNewIssue` appended to each node (if the issue is new)
 */
async function* appendIsNewIssueToRawResults({ currentResults, previousResults }) {
    // Note: If there are no previous results, then we can just return
    // the current results because they are all new.
    //
    // We won't mark each node as `isNewIssue` because client-side we
    // can check if there was a previous result and if there wasn't,
    // we can say all `currentResults` are new. Instead of looping
    // through each node and marking it as `isNewIssue`.
    //
    // This way we can save on performance and not have to loop
    // through each node.
    if (!previousResults) {
        yield* currentResults;
        return;
    }
    const { previousUniqueUrls, previousViolationsMap } = getPreviousViolationsInfo(previousResults);
    for await (const currentResult of currentResults) {
        const hasUrl = previousUniqueUrls.has(currentResult.url);
        // If the current URL is not in the previous unique URLs, then we
        // can just mark all nodes as `isNewIssue` and continue to the
        // next result.
        if (!hasUrl) {
            for (const violation of currentResult.violations) {
                for (const node of violation.nodes) {
                    node.isNewIssue = true;
                }
            }
            yield currentResult;
            continue;
        }
        /**
         * URL exists in the previous results:
         *
         * 1. Loop through each violation in the current result
         * 2. Check if the rule ID exists in the previous violations map
         * 3. If it doesn't, then we can mark all nodes as `isNewIssue`
         *    and continue to the next violation
         * 4. If it does, then we can loop through each node in the
         *    current violation
         * 5. Check if the node exists in the previous violation
         * 6. If it doesn't, then we can mark the node as `isNewIssue`
         *    and continue to the next node
         * 7. If it does, then we can mark the node as `isNewIssue` as
         *    false and continue to the next node
         */
        for (const violation of currentResult.violations) {
            const existingViolation = previousViolationsMap.get(violation.id);
            const { nodes: currentNodes } = violation;
            if (!existingViolation) {
                for (const node of currentNodes) {
                    node.isNewIssue = true;
                }
                continue;
            }
            for (const node of currentNodes) {
                if (node.target && node.target.length) {
                    const currentFingerprints = (0, createFingerprints_1.createFingerprints)({
                        ruleId: violation.id,
                        node,
                        url: currentResult.url
                    });
                    const isExistingIssue = currentFingerprints.some(fingerprint => {
                        return existingViolation.has(fingerprint);
                    });
                    node.isNewIssue = !isExistingIssue;
                }
                else {
                    // no fingerprints available so consider it a new issue because we cannot match it
                    node.isNewIssue = true;
                }
            }
        }
        yield currentResult;
    }
}
/**
 * Helper function to get the previous unique URLs and the previous violations map
 * (id -> nodeInfo target selectors and xpath selectors))
 *
 * @param previousResults - The previous watcher results
 * @returns - The previous unique URLs and the previous violations map
 * (id -> nodeInfo target selectors and xpath selectors)
 */
const getPreviousViolationsInfo = (dedupedPreviousResults) => {
    const previousUniqueUrls = new Set();
    const previousViolationsMap = new Map();
    for (const [ruleId, ruleResult] of dedupedPreviousResults.entries()) {
        const fingerprints = new Set();
        for (const unique of ruleResult.uniques) {
            previousUniqueUrls.add(unique.url);
            if (unique.target && unique.target.length) {
                (0, createFingerprints_1.createFingerprints)({
                    ruleId,
                    node: unique
                }).forEach(fingerprint => fingerprints.add(fingerprint));
            }
        }
        previousViolationsMap.set(ruleId, fingerprints);
    }
    return {
        previousUniqueUrls,
        previousViolationsMap
    };
};
//# sourceMappingURL=appendIsNewIssueToRawResults.js.map